import React, { useState } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { ncbClient } from '../utils/ncbClient'
import { dropboxClient } from '../utils/dropboxClient'

const { FiPlay, FiCopy, FiCode, FiCheck, FiAlertCircle } = FiIcons

const ApiTestForm = ({ apiType }) => {
  const [selectedEndpoint, setSelectedEndpoint] = useState('')
  const [requestBody, setRequestBody] = useState('')
  const [customParams, setCustomParams] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [copySuccess, setCopySuccess] = useState('')

  const ncbEndpoints = [
    { id: 'create', label: 'Create User', method: 'POST', path: '/create/users' },
    { id: 'read', label: 'Read Users', method: 'GET', path: '/read/users' },
    { id: 'readById', label: 'Read User by ID', method: 'GET', path: '/read/users/{id}' },
    { id: 'search', label: 'Search Users', method: 'POST', path: '/search/users' },
    { id: 'update', label: 'Update User', method: 'PUT', path: '/update/users/{id}' },
    { id: 'delete', label: 'Delete User', method: 'DELETE', path: '/delete/users/{id}' }
  ]

  const dropboxEndpoints = [
    { id: 'listFiles', label: 'List Files', method: 'GET', path: '/files/list_folder' },
    { id: 'uploadFile', label: 'Upload File', method: 'POST', path: '/files/upload' },
    { id: 'downloadFile', label: 'Download File', method: 'GET', path: '/files/download' },
    { id: 'refreshToken', label: 'Refresh Token', method: 'POST', path: '/oauth2/token' }
  ]

  const endpoints = apiType === 'ncb' ? ncbEndpoints : dropboxEndpoints

  const getExampleBody = (endpointId) => {
    if (apiType === 'ncb') {
      switch (endpointId) {
        case 'create':
        case 'search':
          return JSON.stringify({ username: 'testuser', password: 'testpass123' }, null, 2)
        case 'update':
          return JSON.stringify({ username: 'updateduser', password: 'newpass123' }, null, 2)
        default:
          return ''
      }
    } else {
      switch (endpointId) {
        case 'listFiles':
          return JSON.stringify({ path: '', recursive: false }, null, 2)
        case 'uploadFile':
          return JSON.stringify({ 
            path: '/test-file.txt', 
            mode: 'add',
            autorename: true 
          }, null, 2)
        case 'refreshToken':
          return JSON.stringify({ 
            grant_type: 'refresh_token',
            refresh_token: 'YOUR_REFRESH_TOKEN' 
          }, null, 2)
        default:
          return ''
      }
    }
  }

  const handleEndpointChange = (endpointId) => {
    setSelectedEndpoint(endpointId)
    setRequestBody(getExampleBody(endpointId))
    setCustomParams('')
  }

  const handleExecuteRequest = async () => {
    if (!selectedEndpoint) return

    setIsLoading(true)
    try {
      let response
      const endpoint = endpoints.find(e => e.id === selectedEndpoint)
      
      if (apiType === 'ncb') {
        const body = requestBody ? JSON.parse(requestBody) : undefined
        const params = customParams ? JSON.parse(customParams) : {}
        response = await ncbClient.request(endpoint.method, endpoint.path, body, params)
      } else {
        const body = requestBody ? JSON.parse(requestBody) : undefined
        response = await dropboxClient.request(endpoint.method, endpoint.path, body)
      }
    } catch (error) {
      console.error('Request failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const generateCurlCommand = () => {
    if (!selectedEndpoint) return ''
    
    const endpoint = endpoints.find(e => e.id === selectedEndpoint)
    const baseUrl = apiType === 'ncb' 
      ? import.meta.env.VITE_NCB_BASE_URL || 'https://openapi.nocodebackend.com'
      : 'https://api.dropboxapi.com/2'
    
    let url = `${baseUrl}${endpoint.path}`
    if (apiType === 'ncb') {
      url += `?Instance=${import.meta.env.VITE_NCB_INSTANCE_ID || '53830_github_cloudflare_pages'}`
    }
    
    let curl = `curl -X ${endpoint.method} "${url}"`
    
    if (apiType === 'ncb') {
      curl += ` \\\n  -H "Authorization: Bearer YOUR_NCB_API_KEY"`
    } else {
      curl += ` \\\n  -H "Authorization: Bearer YOUR_DROPBOX_ACCESS_TOKEN"`
    }
    
    curl += ` \\\n  -H "Content-Type: application/json"`
    
    if (requestBody && (endpoint.method === 'POST' || endpoint.method === 'PUT')) {
      curl += ` \\\n  -d '${requestBody.replace(/'/g, "\\'")}'`
    }
    
    return curl
  }

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopySuccess(type)
      setTimeout(() => setCopySuccess(''), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {apiType === 'ncb' ? 'NoCodeBackend API Tester' : 'Dropbox API Tester'}
        </h2>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${
            apiType === 'ncb' ? 'bg-blue-500' : 'bg-green-500'
          }`}></div>
          <span className="text-sm text-gray-600">
            {apiType === 'ncb' ? 'NCB' : 'Dropbox'}
          </span>
        </div>
      </div>

      <div className="space-y-6">
        {/* Endpoint Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Endpoint
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {endpoints.map((endpoint) => (
              <button
                key={endpoint.id}
                onClick={() => handleEndpointChange(endpoint.id)}
                className={`p-3 text-left rounded-lg border transition-all ${
                  selectedEndpoint === endpoint.id
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{endpoint.label}</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    endpoint.method === 'GET' ? 'bg-green-100 text-green-700' :
                    endpoint.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                    endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {endpoint.method}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1 font-mono">
                  {endpoint.path}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Request Body */}
        {selectedEndpoint && ['create', 'search', 'update', 'uploadFile', 'refreshToken', 'listFiles'].includes(selectedEndpoint) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Request Body (JSON)
            </label>
            <textarea
              value={requestBody}
              onChange={(e) => setRequestBody(e.target.value)}
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter JSON request body..."
            />
          </div>
        )}

        {/* Custom Parameters */}
        {apiType === 'ncb' && selectedEndpoint && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Query Parameters (JSON)
            </label>
            <textarea
              value={customParams}
              onChange={(e) => setCustomParams(e.target.value)}
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder='{"page": 1, "limit": 10, "includeTotal": true}'
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleExecuteRequest}
            disabled={!selectedEndpoint || isLoading}
            className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Executing...
              </>
            ) : (
              <>
                <SafeIcon icon={FiPlay} className="h-4 w-4 mr-2" />
                Execute Request
              </>
            )}
          </button>

          <button
            onClick={() => copyToClipboard(generateCurlCommand(), 'curl')}
            disabled={!selectedEndpoint}
            className="flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={copySuccess === 'curl' ? FiCheck : FiCopy} className="h-4 w-4 mr-2" />
            {copySuccess === 'curl' ? 'Copied!' : 'Copy cURL'}
          </button>
        </div>

        {/* Environment Variables Status */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <SafeIcon icon={FiAlertCircle} className="h-4 w-4 mr-2" />
            Environment Status
          </h3>
          <div className="space-y-2 text-sm">
            {apiType === 'ncb' ? (
              <>
                <div className="flex items-center justify-between">
                  <span>VITE_NCB_BASE_URL</span>
                  <span className={import.meta.env.VITE_NCB_BASE_URL ? 'text-green-600' : 'text-red-600'}>
                    {import.meta.env.VITE_NCB_BASE_URL ? '✓ Set' : '✗ Missing'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>VITE_NCB_INSTANCE_ID</span>
                  <span className={import.meta.env.VITE_NCB_INSTANCE_ID ? 'text-green-600' : 'text-red-600'}>
                    {import.meta.env.VITE_NCB_INSTANCE_ID ? '✓ Set' : '✗ Missing'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>VITE_NCB_API_KEY</span>
                  <span className={import.meta.env.VITE_NCB_API_KEY ? 'text-green-600' : 'text-red-600'}>
                    {import.meta.env.VITE_NCB_API_KEY ? '✓ Set' : '✗ Missing'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span>VITE_DROPBOX_APP_KEY</span>
                  <span className={import.meta.env.VITE_DROPBOX_APP_KEY ? 'text-green-600' : 'text-red-600'}>
                    {import.meta.env.VITE_DROPBOX_APP_KEY ? '✓ Set' : '✗ Missing'}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Note: Dropbox secrets (APP_SECRET, REFRESH_TOKEN) should be configured in Cloudflare Pages Functions for security.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApiTestForm